import {Injectable} from '@angular/core';
import {RegistrationService} from '../../registration.service';
import {isNext, isMPay, isSkycash, isKIOSKPOLIS, isNestBank, isEmbeddedInIframe} from '../../../../utils/utils';
import {UrlParamsService} from '../../url-params.service';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {InsuranceCompanyAdditionalConfig} from '../../../../models/insurance-company-additional-config';
import {InsuranceCompanyCode} from '../../../../../shared/models/insurance-company-code.enum';
import {PaymentMethod} from '../../../../consts/payment-method.enum';
import {ApplicationContextService} from '../../configuration/application-context.service';
import {DeviceService} from '../../../../../shared/services/device.service';

@Injectable({
  providedIn: 'root'
})
export class PaymentMethodsService {

  isNext = isNext();
  private insuranceCompaniesConfig: InsuranceCompanyAdditionalConfig[];
  private nestBankPaymentAllowed: boolean = this.urlParamsService.isMobileOrigin || isEmbeddedInIframe();
  private readonly ICONS_DIR = './assets/images/next-payment-methods';
  private readonly TRANSLATIONS_CONTAINER = 'METHOD_PAYMENT_SECTION';

  private methodsConfig: PaymentMethodConfig[] = [
      {
        method: PaymentMethod.BLIK_ONLINE,
        label: 'METHOD_PAYMENT_SECTION.blikOnline',
        displayLabel: true,
        supported: false,
        leftImage: './assets/images/next-payment-methods/blik-online-transfer.png',
        rightImage: './assets/images/next-payment-methods/przelewy-24.svg',
      },
      {
        method: PaymentMethod.BLIK,
        label: `${this.TRANSLATIONS_CONTAINER}.blik`,
        displayLabel: !isKIOSKPOLIS(),
        supported: isNestBank() ? !this.nestBankPaymentAllowed : true,
        leftImage: isKIOSKPOLIS() ? `${this.ICONS_DIR}/blik.svg` : `${this.ICONS_DIR}/blik.png`,
        rightImage: isKIOSKPOLIS() ? null : './assets/images/next-payment-methods/przelewy-24.svg',
      },
      {
        method: PaymentMethod.APPLE_PAY,
        label: `${this.TRANSLATIONS_CONTAINER}.applePay`,
        displayLabel: false,
        supported: isKIOSKPOLIS(),
        leftImage: `${this.ICONS_DIR}/apple-pay.svg`
      },
      {
        method: PaymentMethod.GOOGLE_PAY,
        label: `${this.TRANSLATIONS_CONTAINER}.googlePay`,
        displayLabel: false,
        supported: isKIOSKPOLIS(),
        leftImage: `${this.ICONS_DIR}/google-pay-mark.svg`,
      },
      {
        method: PaymentMethod.CARD,
        label:  `${this.TRANSLATIONS_CONTAINER}.card`,
        displayLabel: true,
        supported: isNestBank() ? !this.nestBankPaymentAllowed : true,
        leftImage: isKIOSKPOLIS() ? `${this.ICONS_DIR}/card.svg` : `${this.ICONS_DIR}/card.png`,
        rightImage: `${this.ICONS_DIR}/przelewy-24.svg`
      },
      {
        method: PaymentMethod.ONLINE_TRANSFER,
        label:  isKIOSKPOLIS() ? `${this.TRANSLATIONS_CONTAINER}.onlinePayment` : `${this.TRANSLATIONS_CONTAINER}.onlineTransfer`,
        displayLabel: true,
        supported: isNestBank() ? !this.nestBankPaymentAllowed : true,
        leftImage: isKIOSKPOLIS() ? `${this.ICONS_DIR}/online-transfer.svg` : `${this.ICONS_DIR}/online-transfer.png`,
        rightImage: `${this.ICONS_DIR}/przelewy-24.svg`,
      },
      {
        method: PaymentMethod.MPAY,
        label: 'METHOD_PAYMENT_SECTION.mpay',
        displayLabel: true,
        supported: false
      },
      {
        method: PaymentMethod.SKYCASH,
        label: 'METHOD_PAYMENT_SECTION.skycash',
        displayLabel: true,
        supported: false
      },
      {
        method: PaymentMethod.STANDARD_TRANSFER,
        label: 'METHOD_PAYMENT_SECTION.standardTransfer',
        displayLabel: true,
        supported: isNestBank() ? !this.nestBankPaymentAllowed : true,
        leftImage: isKIOSKPOLIS() ? null : './assets/images/next-payment-methods/standard-transfer.png',
        rightImage: isKIOSKPOLIS() ? `${this.ICONS_DIR}/bank.svg` : null
      },
      {
        method: PaymentMethod.TAP_ON_MOBILE,
        label: 'METHOD_PAYMENT_SECTION.contactlessPayment',
        displayLabel: true,
        supported: isNestBank() ? !this.nestBankPaymentAllowed : true,
        leftImage: './assets/images/next-payment-methods/card.png',
        rightImage: './assets/images/next-payment-methods/contactless-payment.svg',
      },
      {
        method: PaymentMethod.NESTBANK,
        label:  `${this.TRANSLATIONS_CONTAINER}.nestbank`,
        displayLabel: true,
        supported: isNestBank() ? this.nestBankPaymentAllowed : false,
        rightImage: './assets/images/logo/nestbank-logo.svg',
        autoSelected: true
      }
  ];

  constructor(private registrationService: RegistrationService,
              private urlParamsService: UrlParamsService,
              private contextService: ApplicationContextService,
              private deviceService: DeviceService) {
  }

  get offerPaymentMethods(): PaymentMethod[] {
    const selectedOffer = this.registrationService.selectedOfferService.selectedOffer;
    if (isNotNullOrUndefined(selectedOffer) && isNotNullOrUndefined(selectedOffer.paymentMethods)) {
      return <PaymentMethod[]>selectedOffer.paymentMethods;
    }
    return [];
  }

  get offerPaymentMethodsDefined(): boolean {
    return !!this.offerPaymentMethods.length;
  }

  get partnerWithOnlinePaymentMethod(): boolean {
    return (isMPay() || isSkycash() || isNestBank()) && this.urlParamsService.isMobileOrigin;
  }

  get displayPaymentsDescription(): boolean {
    return (isMPay() || isSkycash()) && this.urlParamsService.isMobileOrigin;
  }

  get methodPayment(): string {
    return this.registrationService.paymentMethod;
  }

  get supportedMethodsConfig(): PaymentMethodConfig[] {
    return this.methodsConfig.filter(config => config.supported);
  }

  get allowedMethodsConfig(): PaymentMethodConfig[] {
    return this.supportedMethodsConfig.filter(config => this.isMethodAllowed(config.method));
  }

  init() {
    this.createConfig();
    this.registrationService.setPaymentMethodsConfig(this.methodsConfig);
  }

  changeMethodPayment(paymentMethod: string): void {
    this.registrationService.paymentMethod = paymentMethod;
  }

  isMethodAllowed(paymentMethod: PaymentMethod): boolean {
    if (this.isMethodNotAllowedByInsuranceCompany(paymentMethod)) {
      return false;
    }
    return this.isMethodAllowedByOtherRequirements(paymentMethod);
  }

  getMethodConfig(method: PaymentMethod): PaymentMethodConfig {
    return this.methodsConfig.find(config => config.method === method);
  }

  private isMethodNotAllowedByInsuranceCompany(paymentMethod: PaymentMethod): boolean {
    const icConfig = this.insuranceCompaniesConfig.find(config => config.code === this.registrationService.insuranceCompanyCode);
    if (isNotNullOrUndefined(icConfig) && icConfig.excludedPaymentMethods) {
      return icConfig.excludedPaymentMethods().includes(paymentMethod);
    }
    return false;
  }

  private isMethodAllowedByOtherRequirements(paymentMethod: PaymentMethod): boolean {
    const isMethodAllowedByContext = this.contextService.isPaymentMethodAllowed(paymentMethod);
    switch (paymentMethod) {
      case PaymentMethod.CARD:
        return this.getOfferPaymentMethodVisibility(PaymentMethod.CARD, PaymentMethod.BLIK_ONLINE);

      case PaymentMethod.BLIK_ONLINE:
        return this.getOfferPaymentMethodVisibility(PaymentMethod.BLIK_ONLINE);

      case PaymentMethod.BLIK:
        return this.getOfferPaymentMethodVisibility(PaymentMethod.BLIK, PaymentMethod.BLIK_ONLINE);

      case PaymentMethod.ONLINE_TRANSFER:
        return this.getOfferPaymentMethodVisibility(PaymentMethod.ONLINE_TRANSFER, PaymentMethod.BLIK_ONLINE);

      case PaymentMethod.GOOGLE_PAY:
        return this.getOfferPaymentMethodVisibility(PaymentMethod.GOOGLE_PAY, PaymentMethod.BLIK_ONLINE);

      case PaymentMethod.APPLE_PAY:
        return this.deviceService.applePayAvailable && this.getOfferPaymentMethodVisibility(PaymentMethod.APPLE_PAY, PaymentMethod.BLIK_ONLINE);

      case PaymentMethod.STANDARD_TRANSFER:
        return this.getOfferPaymentMethodVisibility(PaymentMethod.STANDARD_TRANSFER);

      case PaymentMethod.MPAY:
        return isMPay()  && this.urlParamsService.isMobileOrigin && isMethodAllowedByContext;

      case PaymentMethod.SKYCASH:
        return isSkycash()  && this.urlParamsService.isMobileOrigin && isMethodAllowedByContext;

      case PaymentMethod.TAP_ON_MOBILE:
        return this.contextService.contextDefined && isMethodAllowedByContext;

      case PaymentMethod.NESTBANK:
        return true;
    }
  }

  private getOfferPaymentMethodVisibility(paymentMethod: PaymentMethod, offerPaymentMethod?: PaymentMethod): boolean {
    if (!isNotNullOrUndefined(offerPaymentMethod)) {
      offerPaymentMethod = paymentMethod;
    }
    if (paymentMethod !== PaymentMethod.STANDARD_TRANSFER && this.partnerWithOnlinePaymentMethod) {
      return false;
    }
    const isMethodAllowedByContext = this.contextService.isPaymentMethodAllowed(paymentMethod);
    if (this.offerPaymentMethodsDefined) {
      return this.offerPaymentMethods.includes(offerPaymentMethod) && isMethodAllowedByContext;
    }
    return isMethodAllowedByContext;
  }

  private createConfig(): void {
    this.insuranceCompaniesConfig = [
      {
        code: InsuranceCompanyCode.GENERALI,
        excludedPaymentMethods: () => this.excludeStandardTransferForNonLoggedUser()
      },
      {
        code: InsuranceCompanyCode.LINK4,
        excludedPaymentMethods: () => this.excludeStandardTransferForNonLoggedUser()
      },
      {
        code: InsuranceCompanyCode.MTU,
        excludedPaymentMethods: () => this.excludeStandardTransferForNonLoggedUser()
      },
      {
        code: InsuranceCompanyCode.BENEFIA,
        excludedPaymentMethods: () => this.excludeStandardTransferForNonLoggedUser()
      },
      {
        code: InsuranceCompanyCode.PROAMA,
        excludedPaymentMethods: () => this.excludeStandardTransferForNonLoggedUser()
      },
      {
        code: InsuranceCompanyCode.UNIQA,
        excludedPaymentMethods: () => this.excludeStandardTransferForNonLoggedUser()
      },
      {
        code: InsuranceCompanyCode.PEVNO,
        excludedPaymentMethods: () => this.excludeStandardTransferForNonLoggedUser()
      },
      {
        code: InsuranceCompanyCode.ALLIANZ,
        excludedPaymentMethods: () => this.excludeStandardTransferForNonLoggedUser()
      },
      {
        code: InsuranceCompanyCode.TUZ,
        excludedPaymentMethods: () => this.excludeStandardTransferForNonLoggedUser()
      },
      {
        code: InsuranceCompanyCode.BEESAFE,
        excludedPaymentMethods: () => this.excludeStandardTransferForNonLoggedUser()
      },
      {
        code: InsuranceCompanyCode.DIRECT,
        excludedPaymentMethods: () => this.excludeStandardTransferForNonLoggedUser()
      },
      {
        code: InsuranceCompanyCode.OMINIMO,
        excludedPaymentMethods: () => [PaymentMethod.STANDARD_TRANSFER]
      },
      {
        code: InsuranceCompanyCode.TRASTI,
        excludedPaymentMethods: () => this.excludeStandardTransferForNonLoggedUser()
      },
      {
        code: InsuranceCompanyCode.ERGO_HESTIA,
        excludedPaymentMethods: () => this.excludeStandardTransferForNonLoggedUser()
      }
    ];
  }

  private excludeStandardTransferForNonLoggedUser(): PaymentMethod[] {
    return this.urlParamsService.isLoggedUserIdParam ? [] : [PaymentMethod.STANDARD_TRANSFER];
  }

}

export interface PaymentMethodConfig {
  method: PaymentMethod;
  label?: string;
  displayLabel: boolean;
  supported: boolean;
  leftImage?: string;
  rightImage?: string;
  hidden?: boolean;
  autoSelected?: boolean; // default - false; if true, registration's payment method will be automatically set as this method
}
